// AdminResearchArcsPage.tsx — Phase 4c: the operator SPINE. Lists arc sends; opening one shows its
// videos in frozen sequence with LIVE stage (teardown/clone/render/gate), and the per-video
// actions: drop/cancel.
// (TF3-1, owner + planning 10-14 05:00) "Clone (edit)", the clone links and the clone render's gate approval are
// DELETED with the clone board (a clone used the source video's frame as the product image, T9-19). A sent arc's
// videos are torn down for research; C1.7's `recreate` makes videos from that structure.
//
// Reachable at /admin/research-arcs, and deep-linked from ArcsTab's post-send note (?arc=<researchArcId>).
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { researchApi, type ResearchArcRollup, type ResearchArcVideoDetail, type ArcVideoStage } from "../../api/research.ts";

const wrap: React.CSSProperties = { padding: 16 };
const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff", marginBottom: 10 };
const btn: React.CSSProperties = { padding: "6px 12px", borderRadius: 7, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const btnDanger: React.CSSProperties = { ...btn, background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" };

const STAGE_LABEL: Record<ArcVideoStage, string> = {
  pending: "Pending", tearing_down: "Tearing down…", torndown: "Torn down",
  cloned: "Cloned (editable)", rendering: "Rendering…", rendered: "Rendered",
  failed: "Failed", cancelled: "Cancelled", awaiting_gate: "⏸ Awaiting approval",
};
const STAGE_COLOR: Record<ArcVideoStage, string> = {
  pending: "#6b7280", tearing_down: "#2563eb", torndown: "#0891b2",
  cloned: "#7c3aed", rendering: "#2563eb", rendered: "#16a34a",
  failed: "#dc2626", cancelled: "#9ca3af", awaiting_gate: "#d97706",
};

export function AdminResearchArcsPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("arc");
  return (
    <div style={wrap}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Arcs → Production</h1>
      <p style={{ color: "#6b7280", fontSize: 13, marginBottom: 16 }}>
        Every arc you sent to production, its videos in release order, and where each one is right now.
      </p>
      {selected
        ? <ArcDetail researchArcId={selected} onBack={() => setParams({})} />
        : <ArcList onOpen={(id) => setParams({ arc: id })} />}
    </div>
  );
}

function ArcList({ onOpen }: { onOpen: (researchArcId: string) => void }) {
  const [rows, setRows] = useState<ResearchArcRollup[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    setErr(null); setRows(null);
    researchApi.listResearchArcs().then(setRows).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, []);
  useEffect(load, [load]);

  if (err) return <div style={{ ...card, color: "#b91c1c" }}>Failed: {err} <button style={btn} onClick={load}>Retry</button></div>;
  if (!rows) return <div style={{ color: "#6b7280" }}>Loading…</div>;
  if (!rows.length) return <div style={card}>No arcs sent to production yet. Select an arc's videos in Research → Arcs and send them.</div>;

  return (
    <div>
      {rows.map((a) => (
        <div key={a.researchArcId + a.circleKey} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 600 }}>{a.circleKey}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {a.total} video{a.total === 1 ? "" : "s"} · sent {new Date(a.linkedAt).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, marginTop: 6, display: "flex", gap: 10 }}>
              {a.rendered > 0 && <span style={{ color: "#16a34a" }}>{a.rendered} rendered</span>}
              {a.rendering > 0 && <span style={{ color: "#2563eb" }}>{a.rendering} in production</span>}
              {a.cloned > 0 && <span style={{ color: "#7c3aed" }}>{a.cloned} editing</span>}
              {a.pending > 0 && <span style={{ color: "#6b7280" }}>{a.pending} awaiting teardown</span>}
              {a.failed > 0 && <span style={{ color: "#dc2626" }}>{a.failed} failed</span>}
              {a.cancelled > 0 && <span style={{ color: "#9ca3af" }}>{a.cancelled} cancelled</span>}
              {a.total > 0 && a.rendered === a.total && <span style={{ color: "#16a34a", fontWeight: 600 }}>✓ all done</span>}
            </div>
          </div>
          <button style={btnPrimary} onClick={() => onOpen(a.researchArcId)}>Open →</button>
        </div>
      ))}
    </div>
  );
}

function ArcDetail({ researchArcId, onBack }: { researchArcId: string; onBack: () => void }) {
  const [videos, setVideos] = useState<ResearchArcVideoDetail[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    setErr(null);
    researchApi.researchArcDetail(researchArcId).then(setVideos).catch((e) => setErr(String(e.message ?? e)));  // catch-reason: the error is shown to the person (setErr)
  }, [researchArcId]);
  useEffect(() => {
    load();
    const iv = setInterval(load, 6000); // live: teardowns land async
    return () => clearInterval(iv);
  }, [load]);

  const cancelRow = async (v: ResearchArcVideoDetail) => {
    if (!window.confirm("Drop this video from the arc?")) return;
    setBusy(v.id);
    try { await researchApi.cancelArcRow(v.id); load(); }
    catch (e) { setNote("Drop failed: " + String((e as Error).message ?? e)); }
    finally { setBusy(null); }
  };

  const cancelAll = async () => {
    if (!window.confirm("Cancel this whole arc send? Not-yet-cloned videos will be cancelled.")) return;
    try { const r = await researchApi.cancelResearchArc(researchArcId); setNote(`Cancelled ${r.cancelled}.`); load(); }
    catch (e) { setNote("Cancel failed: " + String((e as Error).message ?? e)); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <button style={btn} onClick={onBack}>← All arcs</button>
        <button style={btn} onClick={load}>Refresh</button>
        <button style={btnDanger} onClick={cancelAll}>Cancel arc send</button>
        {note && <span style={{ fontSize: 13, color: note.startsWith("Failed") || note.includes("failed") ? "#b91c1c" : "#2f855a" }}>{note}</span>}
      </div>
      {err && <div style={{ ...card, color: "#b91c1c" }}>Failed: {err}</div>}
      {!videos ? <div style={{ color: "#6b7280" }}>Loading…</div> : videos.length === 0 ? (
        <div style={card}>No videos in this arc send.</div>
      ) : (
        <>
        {videos.some((v) => v.stage === "pending" || v.stage === "tearing_down") && (
          <div style={{ padding: 10, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, marginBottom: 10, fontSize: 13, color: "#1e40af" }}>
            Some videos are still being torn down by the funnel — this is normal and takes a few minutes.
            The list refreshes automatically.
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
              <th style={{ padding: "6px 10px" }}>#</th>
              <th style={{ padding: "6px 10px" }}>Role</th>
              <th style={{ padding: "6px 10px" }}>Source</th>
              <th style={{ padding: "6px 10px" }}>Stage</th>
              <th style={{ padding: "6px 10px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map((v) => (
              <tr key={v.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "6px 10px" }}>{v.sequence}</td>
                <td style={{ padding: "6px 10px", color: v.role === "hit" ? "#111827" : "#6b7280" }}>{v.role ?? "—"}</td>
                <td style={{ padding: "6px 10px" }}>
                  {v.sourceUrl
                    ? <a href={v.sourceUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>{v.sourceUrl.replace("https://www.tiktok.com", "")}</a>
                    : "—"}
                </td>
                <td style={{ padding: "6px 10px" }}>
                  <span style={{ fontWeight: 600, color: STAGE_COLOR[v.stage] }}>{STAGE_LABEL[v.stage]}</span>
                  {v.stage === "failed" && (v.teardownError || v.arcError) && (
                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>{(v.teardownError || v.arcError)!.slice(0, 120)}</div>
                  )}
                  {v.teardownReusedAt && v.stage !== "pending" && v.stage !== "tearing_down" && (
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>teardown {new Date(v.teardownReusedAt).toLocaleDateString()}</div>
                  )}
                </td>
                <td style={{ padding: "6px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {(v.stage === "pending" || v.stage === "torndown" || v.stage === "cloned") && (
                    <button style={btnDanger} disabled={busy === v.id} onClick={() => cancelRow(v)}>Drop</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </div>
  );
}
